import { WorldLayoutMoveSchema } from './world-layout.js';
import { WorldNetworkSchema } from './world-network.js';
import { z } from 'zod';
import { GameSettingResultSchema } from './game-setting-rules.js';
import { JvmSettingsSchema, withJvmDefaults } from './jvm.js';
import { WorldWeightSchema, NodeNameSchema } from './nodes.js';
export const SoftwareFamilySchema = z.enum([
  'vanilla',
  'paper',
  'purpur',
  'fabric',
  'neoforge',
  'forge',
  'youer',
  'asyncyouer',
  'mohist',
]);
export type SoftwareFamily = z.infer<typeof SoftwareFamilySchema>;
export const LayoutFamilySchema = z.enum(['vanilla', 'bukkit']);
export type LayoutFamily = z.infer<typeof LayoutFamilySchema>;
export const layoutFamilyOf = (family: SoftwareFamily | 'folia'): LayoutFamily =>
  family === 'paper' || family === 'purpur' || family === 'folia' ? 'bukkit' : 'vanilla';
export const SoftwareSpecSchema = z
  .object({
    family: SoftwareFamilySchema,
    mcVersion: z.string().min(1),
    loaderVersion: z.string().min(1).optional(),
    build: z.string().min(1).optional(),
  })
  .strict();
export type SoftwareSpec = z.infer<typeof SoftwareSpecSchema>;
export const ServerStateSchema = z.enum([
  'provisioning',
  'stopped',
  'starting',
  'running',
  'stopping',
  'error',
  'deleting',
]);
export type ServerState = z.infer<typeof ServerStateSchema>;
export const ResourceSpecObjectSchema = z
  .object({
    cores: z.number().int().positive(),
    memoryMb: z.number().int().positive(),
    heapMb: z.number().int().min(512),
    heapMinMb: z.number().int().min(512).optional(),
    dataDiskGb: z.number().int().positive(),
  })
  .strict()
  .refine((r) => r.heapMb < r.memoryMb, 'heapMb must be less than memoryMb')
  .refine((r) => (r.heapMinMb ?? r.heapMb) <= r.heapMb, {
    message: 'heapMinMb must be less than or equal to heapMb',
    path: ['heapMinMb'],
  });
export const ResourceSpecSchema = ResourceSpecObjectSchema.transform((r) => ({
  ...r,
  heapMinMb: r.heapMinMb ?? r.heapMb,
}));
export type ResourceSpec = z.infer<typeof ResourceSpecSchema>;
export const CreateServerSchema = z
  .object({
    name: z.string().regex(/^[a-z0-9][a-z0-9-]{1,30}$/),
    software: SoftwareSpecSchema,
    resources: ResourceSpecSchema,
    jvm: JvmSettingsSchema.optional(),
    ha: z.boolean().default(true),
    templateVmid: z.number().int().positive().default(9000),
    weight: WorldWeightSchema.default('heavy'),
    preferredNode: NodeNameSchema.optional(),
    proxied: z.boolean().default(false),
  })
  .strict();
export type CreateServer = z.infer<typeof CreateServerSchema>;
export const ServerRecordSchema = CreateServerSchema.extend({
  id: z.string(),
  state: ServerStateSchema,
  revision: z.number().int(),
  node: z.string().optional(),
  vmid: z.number().int().optional(),
  createdAt: z.string(),
  worldNetwork: WorldNetworkSchema.optional(),
});
export type Server = z.infer<typeof ServerRecordSchema>;
export const ServerSchema = ServerRecordSchema.transform((server): Server => withJvmDefaults(server));
export const ViaSchema = z.enum(['web', 'mcp', 'system', 'agent']);
export const JobStatusSchema = z.enum(['queued', 'running', 'succeeded', 'failed', 'cancelled']);
export const JobStepSchema = z.object({
  name: z.string(),
  status: JobStatusSchema,
  startedAt: z.string().optional(),
  finishedAt: z.string().optional(),
  error: z.string().optional(),
});
export type JobStep = z.infer<typeof JobStepSchema>;
export const JobSchema = z.object({
  id: z.string(),
  serverId: z.string(),
  kind: z.enum([
    'create',
    'start',
    'stop',
    'restart',
    'delete',
    'change_software',
    'change_version',
    'change_mods',
    'change_modpack',
    'change_bedrock',
    'change_ports',
    'change_world_generation',
    'regenerate_world',
    'change_game_settings',
    'change_jvm_args',
    'change_resources',
    'move_node',
    'change_world_ip',
    'snapshot',
    'restore_snapshot',
    'delete_snapshot',
    'console_command',
    /** クライアント Mod の起動テスト。ジョブ一覧に表示するだけで、ジョブとしては動かさない */
    'client_pack_test',
  ]),
  result: z
    .object({
      restoreSnapshot: z.string().optional(),
      worldLayoutMoves: z.array(WorldLayoutMoveSchema).optional(),
      gameSettings: z.array(GameSettingResultSchema).optional(),
      consoleOutput: z.string().max(4000).optional(),
      /** クライアント Mod の起動テストが使う VM（動いている間だけ node・vmid が入る） */
      testVm: z
        .object({ node: z.string().optional(), vmid: z.number().int().optional(), memoryMb: z.number() })
        .optional(),
    })
    .optional(),
  status: JobStatusSchema,
  steps: z.array(JobStepSchema),
  requestedBy: z.string(),
  via: ViaSchema,
  createdAt: z.string(),
  startedAt: z.string().optional(),
  finishedAt: z.string().optional(),
  error: z.string().optional(),
});
export type Job = z.infer<typeof JobSchema>;
/** サーバーの操作を止めている（実行中・待ち）ジョブか。クライアント Mod の起動テストは一覧に出すだけで、操作は止めない */
export const blocksOperations = (job: Pick<Job, 'kind' | 'status'>) =>
  job.kind !== 'client_pack_test' && (job.status === 'queued' || job.status === 'running');
export const AuditEntrySchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  requestedBy: z.string(),
  via: ViaSchema,
  operation: z.string(),
  target: z.string(),
  parameters: z.record(z.string(), z.unknown()),
  result: z.string(),
});
export type AuditEntry = z.infer<typeof AuditEntrySchema>;
export const AgentStatusSchema = z.object({ state: ServerStateSchema, additionalDimensions: z.boolean().optional() });
export const StopQuerySchema = z.object({ vm: z.enum(['true', 'false']).optional() }).strict();
export const ServerIdParamsSchema = z.object({ id: z.string().min(1) }).strict();
export const IdempotencyKeySchema = z.string().min(1).max(200).optional();
